import { useId, useRef, type ReactNode } from 'react';
import { Button, Modal } from '@shared/components';

export interface ConfirmActionModalProps {
    isOpen: boolean;
    /** Visible heading — also the accessible name via the shared Modal's `ariaLabelledBy`. */
    title: string;
    /** Body text; supports plain strings and rich content (e.g. dynamic names). */
    message: ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    confirmVariant?: 'danger' | 'warning' | 'primary';
    /** Disables both buttons and swaps the Confirm label to a busy state; drives the double-submit guard. */
    isPending?: boolean;
    onConfirm: () => void;
    /** Cancel button, Escape, backdrop, and the Modal's close (X) button all route here.
     *  Never fires while `isPending`: a dismissed dialog would let a second action start. */
    onClose: () => void;
}

/** Confirmation dialog for destructive actions, wrapping the shared `Modal` + `Button`. */
export const ConfirmActionModal: React.FC<ConfirmActionModalProps> = ({
    isOpen,
    title,
    message,
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    confirmVariant = 'danger',
    isPending = false,
    onConfirm,
    onClose,
}) => {
    const headingId = useId();
    const cancelWrapperRef = useRef<HTMLSpanElement>(null);

    // Disabled buttons alone leave Escape, the backdrop and the X open as dismiss paths.
    const handleClose = () => {
        if (isPending) return;
        onClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            showCloseButton={!isPending}
            ariaLabelledBy={headingId}
            className="max-w-md p-6"
            initialFocusRef={cancelWrapperRef}
        >
            <h2 id={headingId} className="text-lg font-semibold mb-2">
                {title}
            </h2>
            <div className="text-sm text-mp-muted mb-4">{message}</div>
            <div className="flex justify-end gap-2">
                <span ref={cancelWrapperRef}>
                    <Button type="button" variant="clear" disabled={isPending} onClick={handleClose}>
                        {cancelLabel}
                    </Button>
                </span>
                <Button type="button" variant={confirmVariant} disabled={isPending} onClick={onConfirm}>
                    {isPending ? 'Working…' : confirmLabel}
                </Button>
            </div>
        </Modal>
    );
};
